import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { INTERACTION_TYPES } from '@cult/domain';
import type { PublicDirectFeedback } from '@cult/schemas';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { App as AntApp, Button, Popconfirm, Space, Typography } from 'antd';
import { BodyRender, EditWindowNotice } from '../../components';
import { AttachmentsView } from '../../features/attachments';
import { useDeleteDirectFeedback, useDirectFeedback } from '../../features/direct-feedback';
import {
  DetailSection,
  INTERACTION_FEEDS,
  INTERACTION_TYPE_META,
  InteractionDetailAside,
  InteractionDetailError,
  InteractionDetailHeader,
  InteractionDetailLayout,
  InteractionDetailSkeleton,
} from '../../features/interactions';
import { ReferenceTags } from '../../features/reference';
import { useCurrentUser } from '../../state/use-current-user';

const { Title } = Typography;

const AuthorActions = ({ feedback }: { feedback: PublicDirectFeedback }) => {
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const deleteFeedback = useDeleteDirectFeedback();

  const onDelete = () =>
    deleteFeedback.mutate(feedback, {
      onSuccess: () => {
        message.success('Feedback deleted');
        navigate({ to: INTERACTION_FEEDS[INTERACTION_TYPES.DIRECT_FEEDBACK].feedPath });
      },
      onError: (err) => message.error(`Couldn't delete feedback: ${err.message}`),
    });

  return (
    <Space>
      <Button
        size="small"
        icon={<EditOutlined />}
        onClick={() => navigate({ to: '/feedback/$id/edit', params: { id: String(feedback.number) } })}
      >
        Edit
      </Button>
      <Popconfirm
        title="Delete this feedback?"
        description="This also removes its attachments. It can't be undone."
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleteFeedback.isPending }}
        onConfirm={onDelete}
      >
        <Button danger size="small" icon={<DeleteOutlined />}>
          Delete
        </Button>
      </Popconfirm>
    </Space>
  );
};

const FeedbackDetailPage = () => {
  const { id } = Route.useParams();
  const feedbackQuery = useDirectFeedback(id);
  const userId = useCurrentUser((s) => s.user?.id);

  if (feedbackQuery.isPending) return <InteractionDetailSkeleton />;
  if (feedbackQuery.isError) {
    return (
      <InteractionDetailError
        type={INTERACTION_TYPES.DIRECT_FEEDBACK}
        title="Couldn't load feedback"
        message={feedbackQuery.error.message}
      />
    );
  }

  const feedback = feedbackQuery.data;

  return (
    <InteractionDetailLayout
      type={INTERACTION_TYPES.DIRECT_FEEDBACK}
      accent={INTERACTION_TYPE_META[INTERACTION_TYPES.DIRECT_FEEDBACK].accent}
      notice={
        feedback.author.id === userId ? (
          <EditWindowNotice
            createdAt={feedback.createdAt}
            noun="feedback"
            actions={<AuthorActions feedback={feedback} />}
          />
        ) : null
      }
      aside={
        <InteractionDetailAside
          author={feedback.author}
          receiver={feedback.receiver}
          createdAt={feedback.createdAt}
          receiverLabel="Recipient"
        />
      }
    >
      <InteractionDetailHeader
        type={INTERACTION_TYPES.DIRECT_FEEDBACK}
        author={feedback.author}
        receiver={feedback.receiver}
        createdAt={feedback.createdAt}
      />

      <Title level={2} style={{ margin: '12px 0' }}>
        {feedback.title}
      </Title>

      <ReferenceTags tags={feedback} style={{ marginBottom: 4 }} />

      <DetailSection title="What happened">
        <BodyRender source={feedback.whatHappened} />
      </DetailSection>

      <AttachmentsView attachments={feedback.attachments} />
    </InteractionDetailLayout>
  );
};

export const Route = createFileRoute('/_auth/feedback/$id')({
  component: FeedbackDetailPage,
});
